<script setup>
import { onMounted, ref, watch, nextTick, onUnmounted } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
// import AppAutocomplete from "@/@core/components/app-form-elements/AppAutocomplete.vue";
const router = useRouter();
const $loading = useLoading(loaderData);

const workflowData = ref({
    title: "",
    description: "",
    status: "draft",
    assignmentRules: []
});

const statusOptions = [
    {
        title: "Draft",
        value: "draft",
    },
    {
        title: "Active",
        value: "active",
    },
    {
        title: "Inactive",
        value: "inactive",
    },  
];

const submitBtn = ref(1);

const triggers = ref([]);
const workflows = ref([]);
const workflowSteps = ref({});
const users = ref([]);
const teams = ref([]);
const roles = ref([]);

const isRuleDialogVisible = ref(false);
const isPlaceholdersDialogVisible = ref(false);
const ruleAssignmentType = ref('user'); // 'user' | 'team'
const selectedAssignmentUser = ref(null);
const selectedAssignmentTeam = ref(null);
const currentRule = ref({
    name: '',
    workflow_id: null,
    workflow_step_id: null,
    document_template_id: null,
    trigger: [],
    assigned_to: {
        users: [],
        teams: [],
        // roles: [] // role assignment commented out
    }
});

watch(ruleAssignmentType, () => {
    selectedAssignmentUser.value = null;
    selectedAssignmentTeam.value = null;
});

const placeholders = [
    {
        placeholder: '{{DOCUMENT_TEMPLATE_NAME}}',
        description: 'To add the document template name'
    },
    {
        placeholder: '{{WORKFLOW_NAME}}',
        description: 'To add the workflow name'
    },
    {
        placeholder: '{{WORKFLOW_STEP_NAME}}',
        description: 'To add the workflow step name'
    },
    {
        placeholder: '{{WORKFLOW_STEP_APPROVER}}',
        description: 'To add the name of the person who approved the workflow step'
    },
    {
        placeholder: '{{WORKFLOW_STEP_REJECTOR}}',
        description: 'To add the name of the person who rejected the workflow step'
    },
    {
        placeholder: '{{ENVELOPE_RECIPIENTS}}',
        description: 'Shows the names and emails of all recipients in an envelope'
    },
    {
        placeholder: '{{ENVELOPE_DOCUMENTS}}',
        description: 'Shows the names of all documents sent in an envelope'
    }
];

const copyPlaceholder = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Placeholder copied to clipboard', {
        autoClose: 2000,
    });
};

// Add watch on trigger to reset values when it changes
watch(() => currentRule.value.trigger, (newTrigger) => {
    // Reset workflow and workflow step if new trigger doesn't need them
    if (!needsWorkflow(newTrigger)) {
        currentRule.value.workflow_id = null;
        currentRule.value.workflow_step_id = null;
    }
    
    // Reset document template if new trigger doesn't need it
    if (!needsDocumentTemplate(newTrigger)) {
        currentRule.value.document_template_id = null;
    }
});

// Track if we're in edit mode and which rule we're editing
const editMode = ref(false);
const editRuleIndex = ref(-1);

onMounted(async () => {
    fetchTriggers();
    fetchWorkflows();
    fetchDocumentTemplates();
    getUsers();
    getTeams();
    getRoles();
});
const documentTemplates = ref([]);
const documentTemplateSearchQuery = ref('');
watch(documentTemplateSearchQuery, () => {
    fetchDocumentTemplates();
});
const fetchDocumentTemplates = () => {
    let includes = workflowData.value.assignmentRules.map(rule => rule.document_template_id).flat();
    includes.push(currentRule.value.document_template_id);
    includes = [...new Set(includes)];
    axios
        .get(`/workflow_document_templates?search_query=${documentTemplateSearchQuery.value}&includes=${includes.join(',')}`)
        .then((response) => {
            if (response.status == "success") {
                documentTemplates.value = response.data.templates.data;
            }
        });
};
const fetchTriggers = () => {
    axios
        .get("/workflow_task_triggers")
        .then((response) => {
            if (response.status == "success") {
                triggers.value = response.data.triggers;
            }
        });
};

const workflowSearchQuery = ref('');
watch(workflowSearchQuery, () => {
    fetchWorkflows();
});
const fetchWorkflows = () => {
    let includes = workflowData.value.assignmentRules.map(rule => rule.workflow_id).flat();
    includes.push(currentRule.value.workflow_id);
    includes = [...new Set(includes)];
    axios
        .get(`/workflows?search_query=${workflowSearchQuery.value}&includes=${includes.join(',')}`)
        .then((response) => {
            if (response.status == "success") {
                workflows.value = response.data.workflows.data;
                fetchWorkflowSteps();
            }
        });
};

const fetchWorkflowSteps = () => {
    workflows.value.forEach(workflow => {
        axios
            .get(`/workflow_steps?workflow_id=${workflow.id}`)
            .then((response) => {
                if (response.status == "success") {
                    workflowSteps.value[workflow.id] = response.data.workflow_steps;
                }
            });
    });
};

const userSearchQuery = ref('');
watch(userSearchQuery, () => {
    getUsers();
});
const getUsers = () => {
    let includes = workflowData.value.assignmentRules.map(rule => rule.assigned_to.users).flat();
    includes.push(currentRule.value.assigned_to.users);
    includes = [...new Set(includes)];
    axios
        .get(`/users?search_query=${userSearchQuery.value}&includes=${includes.join(',')}`)
        .then((response) => {
            if (response.status === "success") {
                users.value = Object.entries(response.data.users.data).map(
                    ([index, value]) => {
                        return {
                            value: value.id,
                            title: value.first_name + " " + value.last_name,
                        };
                    }
                );
            }
        });
};

const teamSearchQuery = ref('');
watch(teamSearchQuery, () => {
    getTeams();
});
const getTeams = () => {
    let includes = workflowData.value.assignmentRules.map(rule => rule.assigned_to.teams).flat();
    includes.push(currentRule.value.assigned_to.teams);
    includes = [...new Set(includes)];
    axios
        .get(`/teams?search_query=${teamSearchQuery.value}&includes=${includes.join(',')}`)
        .then(async (response) => {
            if (response.status == "success") {
                teams.value = Object.entries(response.data.teams.data).map(
                    ([index, value]) => {
                        return {
                            value: value.id,
                            title: value.name,
                        };
                    }
                );
            }
        });
};

const getRoles = () => {
    axios
        .get(`/get-all-roles`)
        .then(async (response) => {
            if (response.status == "success") {
                roles.value = Object.entries(response.data.roles).map(
                    ([index, value]) => {
                        return {
                            value: index,
                            title: value,
                        };
                    }
                );
            }
        });
};

const createWorkflowTask = async () => {
    let loader = $loading.show();
    submitBtn.value = 0;
    axios
        .post("/workflow_tasks", workflowData.value)
        .then((response) => {
            if (response.status == "success") {
                toast.success(response.message, {
                    autoClose: 5000,
                });
                router.push({ name: "workflow-tasks-list"});
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
            submitBtn.value = 1;
        });
};

const discardChanges = () => {
    router.push({ name: "workflow-tasks-list" });
}

const openRuleDialog = () => {
    // Reset to add mode
    editMode.value = false;
    editRuleIndex.value = -1;
    ruleAssignmentType.value = 'user';
    selectedAssignmentUser.value = null;
    selectedAssignmentTeam.value = null;
    
    currentRule.value = {
        name: '',
        workflow_id: null,
        workflow_step_id: null,
        trigger: [],
        assigned_to: {
            users: [],
            teams: [],
        }
    };
    isRuleDialogVisible.value = true;
}

const needsDocumentTemplate = (trigger) => {
    return ['documents_envelope_sent', 'documents_envelope_completed'].includes(trigger);
}

// Add function to check for duplicate rules
const isDuplicateRule = (rule) => {
    return workflowData.value.assignmentRules.some((existingRule, index) => {
        // Skip comparing with the rule being edited
        if (editMode.value && index === editRuleIndex.value) {
            return false;
        }
        
        // First check if triggers match
        if (existingRule.trigger !== rule.trigger) return false;
        
        // For workflow-related triggers, check workflow and step
        if (needsWorkflow(rule.trigger)) {
            if (existingRule.workflow_id !== rule.workflow_id) return false;
            if (needsWorkflowStep(rule.trigger) && existingRule.workflow_step_id !== rule.workflow_step_id) return false;
        }
        
        // For document template triggers, check document template
        if (needsDocumentTemplate(rule.trigger)) {
            if (existingRule.document_template_id !== rule.document_template_id) return false;
        }
        
        return true;
    });
};

const saveRule = () => {
    // Check if at least one assignment is selected
    if (ruleAssignmentType.value === 'user' && !selectedAssignmentUser.value) {
        toast.error('Please select a user to assign', {
            autoClose: 2000,
        });
        return;
    }
    if (ruleAssignmentType.value === 'team' && !selectedAssignmentTeam.value) {
        toast.error('Please select a team to assign', {
            autoClose: 2000,
        });
        return;
    }

    // Build assigned_to based on assignment type
    currentRule.value.assigned_to = {
        users: ruleAssignmentType.value === 'user' && selectedAssignmentUser.value
            ? [selectedAssignmentUser.value] : [],
        teams: ruleAssignmentType.value === 'team' && selectedAssignmentTeam.value
            ? [selectedAssignmentTeam.value] : [],
    };

    // Check for duplicate rule
    if (isDuplicateRule(currentRule.value)) {
        toast.error('A rule with these conditions already exists', {
            autoClose: 2000,
        });
        return;
    }

    if (editMode.value && editRuleIndex.value >= 0) {
        // Update existing rule
        workflowData.value.assignmentRules[editRuleIndex.value] = { ...currentRule.value };
    } else {
        // Add new rule
        workflowData.value.assignmentRules.push({ ...currentRule.value });
    }
    isRuleDialogVisible.value = false;
}

const editRule = (index) => {
    editMode.value = true;
    editRuleIndex.value = index;
    const rule = workflowData.value.assignmentRules[index];
    currentRule.value = { ...rule, assigned_to: { ...rule.assigned_to } };

    // Set assignment type and selected value from existing rule
    if (rule.assigned_to.users?.length) {
        ruleAssignmentType.value = 'user';
        selectedAssignmentUser.value = rule.assigned_to.users[0];
        selectedAssignmentTeam.value = null;
    } else if (rule.assigned_to.teams?.length) {
        ruleAssignmentType.value = 'team';
        selectedAssignmentTeam.value = rule.assigned_to.teams[0];
        selectedAssignmentUser.value = null;
    } else {
        ruleAssignmentType.value = 'user';
        selectedAssignmentUser.value = null;
        selectedAssignmentTeam.value = null;
    }
    isRuleDialogVisible.value = true;
}

const deleteRule = (index) => {
    workflowData.value.assignmentRules.splice(index, 1);
}

const needsWorkflow = (trigger) => {
    return ['workflow_started', 'workflow_completed', 'workflow_step_completed', 
            'workflow_step_approved', 'workflow_step_rejected'].includes(trigger);
}

const needsWorkflowStep = (trigger) => {
    return ['workflow_step_completed', 'workflow_step_approved', 'workflow_step_rejected'].includes(trigger);
}

definePage({ meta: { requiresAuth: true } });
</script>

<template>
    <div>
        <div class="d-flex flex-wrap justify-start justify-sm-space-between gap-y-4 gap-x-6 mb-6">
            
            <div class="d-flex flex-column justify-center">
                <h2>
                    <strong class="oswald">Workflow Task Builder</strong>
                </h2>
                <span class="lato-light">You can add new workflow task on this page</span>
            </div>

            <div class="d-flex gap-4 align-center flex-wrap">
                <VBtn @click="isPlaceholdersDialogVisible = true" variant="text" color="primary">
                    <VIcon icon="tabler-help" class="me-2" />
                    Placeholders
                </VBtn>
                <VBtn @click.prevent="discardChanges" variant="tonal" color="dark">
                    Discard
                </VBtn>
                <VBtn @click.prevent="createWorkflowTask">Create</VBtn>
            </div>
        </div>

        <VRow>
            <VCol cols="12" :md="12" >
                <VCard class="mb-6 form_fields_dropzone_create_workflow " title="Task Information">
                    <VCardText>

                        <VForm class="mt-6" @submit.prevent="createWorkflowTask()">
                            <VRow>
                                
                                <VCol cols="12" md="6">
                                    <AppTextField v-model="workflowData.title" label="Title *"
                                        placeholder="Title" :rules="[requiredValidator]" />
                                </VCol>

                                <VCol cols="12" md="3">
                                    <AppSelect v-model="workflowData.status" :items="statusOptions" label="Status *"
                                        placeholder="Status" :rules="[requiredValidator]" />
                                </VCol>
                                
                                <VCol cols="12" md="12">
                                    <AppTextarea rows="3" v-model="workflowData.description" label="Description"
                                        placeholder="Description" />
                                </VCol>

                            </VRow>
                            
                        </VForm>

                    </VCardText>
                </VCard>

            </VCol>

        </VRow>

        <div class="d-flex flex-wrap justify-start justify-sm-space-between gap-y-4 gap-x-6 mb-6">
            
            <div class="d-flex flex-column justify-center">
                <h2>
                    <strong class="oswald">Task Assignment Rules</strong>
                </h2>
                <span class="lato-light">You can add new task assignment rules below</span>
            </div>
            
            <VBtn
                icon
                size="small"
                color="primary" variant="tonal"
                @click="openRuleDialog"
            >
                <VIcon
                    size="22"
                    icon="tabler-plus"
                />
            </VBtn>

        </div>

        <div v-for="(rule, index) in workflowData.assignmentRules" :key="index" class="step mt-5">
            <VCard class="mb-3 py-5 px-3 ">

                <div class="d-flex justify-end gap-1 step_action_buttons">
                    <VBtn
                        icon
                        size="x-small"
                        color="primary" variant="tonal"
                        @click="editRule(index)"
                    >
                        <VIcon size="20" icon="tabler-pencil" />
                    </VBtn>
                    <VBtn
                        icon
                        size="x-small"
                        color="primary" variant="tonal"
                        @click="deleteRule(index)"
                    >
                        <VIcon size="20" icon="tabler-trash" />
                    </VBtn>
                </div>

                <VRow class="" style="padding: 0px !important; margin: 0px !important;margin-left: 10px;">
                    <VCol
                        style="padding: 0px !important; margin: 0px !important;"
                        cols="12"
                        md="3"
                    >
                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 " style="padding: 0px !important; margin: 0px !important;">
                            <h3 class="oswald">
                                Rule Name
                            </h3>
                            <div>
                                <span class="text-primary lato-light">{{ rule.name }}</span>
                            </div>
                        </VCardText>
                    </VCol>

                    <VCol
                        style="padding: 0px !important; margin: 0px !important;"
                        cols="12"
                        md="3"
                        v-if="rule.workflow_id"
                    >
                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 " style="padding: 0px !important; margin: 0px !important;">
                            <h3 class="oswald">
                                Workflow
                            </h3>
                            <div>
                                <span class="text-primary lato-light">
                                    {{ workflows.find(w => w.id === rule.workflow_id)?.title || 'Unknown Workflow' }}
                                </span>
                            </div>
                        </VCardText>
                    </VCol>

                    <VCol
                        style="padding: 0px !important; margin: 0px !important;"
                        cols="12"
                        md="3"
                        v-if="rule.workflow_step_id"
                    >
                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 " style="padding: 0px !important; margin: 0px !important;">
                            <h3 class="oswald">
                                Workflow Step
                            </h3>
                            <div>
                                <span class="text-primary lato-light">
                                    {{ workflowSteps[rule.workflow_id]?.find(s => s.id === rule.workflow_step_id)?.title || 'Unknown Step' }}
                                </span>
                            </div>
                        </VCardText>
                    </VCol>

                </VRow>
            
                <VRow style="padding: 0px !important; margin: 0px !important;margin-top: 15px; margin-left: 10px;">
                    <VCol
                        cols="12"
                        md="12"
                        style="padding: 0px !important; margin: 0px !important;"
                    >
                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1" style="padding: 0px !important; margin: 0px !important;">
                            <h3 class="oswald">
                                Trigger
                            </h3>
                            <div>
                                <div class="d-flex gap-1 flex-wrap">
                                    <VChip
                                        :label="false"
                                        color="primary"
                                    >
                                        {{ triggers.find(t => t.value === rule.trigger)?.title || rule.trigger }}
                                    </VChip>
                                </div>
                            </div>
                        </VCardText>
                    </VCol>
                </VRow>

                <VRow class="" style="padding: 0px !important; margin: 0px !important; margin-top: 15px; margin-left: 10px;">
                    <VCol
                        style="padding: 0px !important; margin: 0px !important;"
                        cols="12"
                        md="12"
                    >
                        <VCardText class="d-flex gap-y-2 flex-column ma-0 pa-2 text-body-1 " style="padding: 0px !important; margin: 0px !important;">
                            <h3 class="oswald">
                                Assigned to
                            </h3>
                            <div>
                                <div class="d-flex gap-1 flex-wrap">
                                    <VChip
                                        v-if="rule.assigned_to.users.length > 0"
                                        :label="false"
                                        color="primary"
                                    >
                                        1 User
                                    </VChip>

                                    <VChip
                                        v-if="rule.assigned_to.teams.length > 0"
                                        :label="false"
                                        color="primary"
                                    >
                                        1 Team
                                    </VChip>

                                    <!-- Roles chip commented out -->

                                </div>
                            </div>
                        </VCardText>
                    </VCol>
                </VRow>

            </VCard>
        </div>

        <!-- Task Assignment Rule Dialog -->
        <VDialog
            v-model="isRuleDialogVisible"
            max-width="900px"
            persistent
        >
            <VCard>
                <VCardTitle class="d-flex align-center justify-space-between py-3 px-5">
                    <h2 class="text-h5">
                        {{ editMode ? 'Edit Task Assignment Rule' : 'Add Task Assignment Rule' }}
                    </h2>
                    <VBtn
                        icon
                        size="small"
                        variant="text"
                        color="default"
                        @click="isRuleDialogVisible = false"
                    >
                        <VIcon
                            size="24"
                            icon="tabler-x"
                        />
                    </VBtn>
                </VCardTitle>

                <VDivider />

                <VCardText class="pa-5">
                    <VForm @submit.prevent="saveRule">
                        <VRow>
                            <VCol cols="12">
                                <AppTextField
                                    v-model="currentRule.name"
                                    label="Rule Name *"
                                    placeholder="Enter a name for this rule"
                                    :rules="[requiredValidator]"
                                />
                            </VCol>

                            <VCol cols="12">
                                <AppSelect
                                    v-model="currentRule.trigger"
                                    :items="triggers"
                                    clearable
                                    label="Trigger *"
                                    placeholder="Select a trigger"
                                    :rules="[requiredValidator]"
                                />
                            </VCol>

                            <VCol 
                                cols="12" 
                                md="6" 
                                v-if="needsWorkflow(currentRule.trigger)"
                            >
                                <AppAutocomplete
                                    v-model:search="workflowSearchQuery"
                                    v-model="currentRule.workflow_id"
                                    :items="workflows.map(w => ({title: w.title, value: w.id}))"
                                    label="Workflow"
                                    clearable
                                    placeholder="Select workflow"
                                />
                            </VCol>

                            <VCol 
                                cols="12" 
                                md="6" 
                                v-if="currentRule.workflow_id && needsWorkflowStep(currentRule.trigger)"
                            >
                                <AppSelect
                                    v-model="currentRule.workflow_step_id"
                                    :items="(workflowSteps[currentRule.workflow_id] || []).map(s => ({title: s.title, value: s.id}))"
                                    label="Workflow Step"
                                    clearable
                                    placeholder="Select workflow step"
                                />
                            </VCol>

                            <VCol 
                                cols="12" 
                                md="6" 
                                v-if="needsDocumentTemplate(currentRule.trigger)"
                            >
                                <AppAutocomplete
                                    v-model:search="documentTemplateSearchQuery"
                                    v-model="currentRule.document_template_id"
                                    :items="documentTemplates.map(t => ({title: t.name, value: t.id}))"
                                    label="Document Template"
                                    clearable
                                    placeholder="Select document template"
                                />
                            </VCol>

                            <VCol cols="12" class="mt-5">
                                <h3 class="text-h6 mb-2">Assign To</h3>
                            </VCol>

                            <VCol cols="12">
                                <VRadioGroup v-model="ruleAssignmentType" inline>
                                    <VRadio label="User" value="user" />
                                    <VRadio label="Team" value="team" />
                                </VRadioGroup>
                            </VCol>

                            <VCol cols="12" v-if="ruleAssignmentType === 'user'">
                                <AppAutocomplete
                                    v-model:search="userSearchQuery"
                                    v-model="selectedAssignmentUser"
                                    :items="users"
                                    label="User"
                                    clearable
                                    placeholder="Select user"
                                />
                            </VCol>

                            <VCol cols="12" v-else>
                                <AppAutocomplete
                                    v-model:search="teamSearchQuery"
                                    v-model="selectedAssignmentTeam"
                                    :items="teams"
                                    label="Team"
                                    clearable
                                    placeholder="Select team"
                                />
                            </VCol>

                            <!-- Role assignment commented out:
                            <VCol cols="12">
                                <AppSelect
                                    v-model="currentRule.assigned_to.roles"
                                    :items="roles"
                                    label="Roles"
                                    multiple
                                    chips
                                    clearable
                                    placeholder="Select roles"
                                />
                            </VCol>
                            -->
                        </VRow>
                    </VForm>
                </VCardText>

                <VDivider />

                <VCardActions class="pa-5 d-flex justify-end">
                    <VBtn
                        variant="tonal"
                        color="light"
                        @click="isRuleDialogVisible = false"
                    >
                        Cancel
                    </VBtn>
                    <VBtn
                        color="primary"
                        variant="tonal"
                        :disabled="!currentRule.name || !currentRule.trigger"
                        @click="saveRule"
                    >
                        {{ editMode ? 'Update' : 'Save' }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>

        <!-- Placeholders Dialog -->
        <VDialog
            v-model="isPlaceholdersDialogVisible"
            max-width="700px"
        >
            <VCard>
                <VCardTitle class="d-flex align-center justify-space-between py-3 px-5">
                    <h2 class="text-h5">
                        Available Placeholders
                    </h2>
                    <VBtn
                        icon
                        size="small"
                        variant="text"
                        color="default"
                        @click="isPlaceholdersDialogVisible = false"
                    >
                        <VIcon
                            size="24"
                            icon="tabler-x"
                        />
                    </VBtn>
                </VCardTitle>

                <VDivider />

                <VCardText class="pa-5">
                    <p class="text-body-1 mb-6">
                        These placeholders can be used in your task title or description. When a task is created, the system will automatically replace each placeholder with its corresponding dynamic value.
                    </p>

                    <div class="d-flex flex-column gap-4">
                        <div v-for="(item, index) in placeholders" :key="index" class="d-flex flex-column">
                            <div class="d-flex align-center gap-2">
                                <span class="text-h6">{{ index + 1 }}.</span>
                                <VChip
                                    color="primary"
                                    variant="tonal"
                                    class="font-weight-medium placeholder-chip"
                                    @click="copyPlaceholder(item.placeholder)"
                                >
                                    {{ item.placeholder }}
                                </VChip>
                            </div>
                            <span class="text-body-2 text-medium-emphasis ms-6">{{ item.description }}</span>
                        </div>
                    </div>
                </VCardText>

                <VDivider />

                <VCardActions class="pa-5 d-flex justify-end">
                    <VBtn
                        color="light"
                        variant="tonal"
                        @click="isPlaceholdersDialogVisible = false"
                    >
                        Close
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>

    </div>
</template>

<style scoped>
.step_action_buttons{
    position: absolute;
    top:10px;
    right:20px;
}

.placeholder-chip {
    cursor: pointer;
    transition: all 0.2s ease;
}

.placeholder-chip:hover {
    opacity: 0.8;
}
</style>